﻿import { useEffect, useState } from 'react';
import {
  Page, Card, Button, Banner, Badge, Text, Select,
  DataTable, EmptyState, Spinner, Layout, BlockStack,
  InlineStack, Box, Pagination, InlineGrid,
} from '@shopify/polaris';
import { InventoryIcon, AlertTriangleIcon, ListBulletedIcon } from '@shopify/polaris-icons';
import { useInventoryForecast } from '../../hooks/useInventoryForecast';
import type { InventoryForecast, InventoryUrgency } from '../../api/inventoryForecast';
import { StatCard } from '../shared/StatCard';
import { IconBadge } from '../shared/IconBadge';

function urgencyBadge(urgency: InventoryUrgency) {
  const map: Record<InventoryUrgency, 'critical' | 'attention' | 'info'> = {
    high:   'critical',
    medium: 'attention',
    low:    'info',
  };
  return <Badge tone={map[urgency] ?? 'info'}>{urgency}</Badge>;
}

interface DetailProps {
  forecast: InventoryForecast;
  onBack: () => void;
  onDelete: (id: number) => void;
}

function ForecastDetail({ forecast, onBack, onDelete }: DetailProps) {
  return (
    <Page fullWidth
      title={`Inventory Forecast: ${forecast.period_start} – ${forecast.period_end}`}
      backAction={{ content: 'All Forecasts', onAction: onBack }}
      secondaryActions={[{
        content: 'Delete',
        destructive: true,
        onAction: () => { onDelete(forecast.id); onBack(); },
      }]}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            <Card>
              <BlockStack gap="100">
                <Text as="p" variant="bodySm" tone="subdued">Orders analysed</Text>
                <Text as="p" variant="heading2xl" fontWeight="bold">{forecast.orders_analyzed}</Text>
              </BlockStack>
            </Card>

            {forecast.ai_summary && (
              <Card>
                <BlockStack gap="200">
                  <Text as="h2" variant="headingMd">Summary</Text>
                  <Text as="p" variant="bodyMd">{forecast.ai_summary}</Text>
                </BlockStack>
              </Card>
            )}

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Reorder Recommendations</Text>
                {forecast.ai_recommendations.length === 0 ? (
                  <Text as="p" tone="subdued">No recommendations generated.</Text>
                ) : (
                  <BlockStack gap="300">
                    {forecast.ai_recommendations.map((r, i) => (
                      <Card key={i} background="bg-surface-secondary">
                        <BlockStack gap="150">
                          <InlineStack gap="300" blockAlign="center" wrap={false}>
                            <IconBadge icon={InventoryIcon} color={r.urgency === 'high' ? 'orange' : 'indigo'} />
                            <BlockStack gap="050">
                              <Text as="h3" variant="headingSm">{r.product_title}</Text>
                              {urgencyBadge(r.urgency)}
                            </BlockStack>
                          </InlineStack>
                          <Text as="p" variant="bodySm" fontWeight="semibold">
                            Suggested reorder quantity: {r.suggested_reorder_quantity}
                          </Text>
                          <Text as="p" variant="bodyMd">{r.rationale}</Text>
                          <Text as="p" variant="bodySm" tone="success">
                            Expected impact: {r.expected_impact}
                          </Text>
                        </BlockStack>
                      </Card>
                    ))}
                  </BlockStack>
                )}
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Reorder Candidates</Text>
                {forecast.reorder_candidates.length === 0 ? (
                  <Text as="p" tone="subdued">No candidates found.</Text>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'numeric', 'numeric', 'numeric']}
                    headings={['Product', 'In Stock', 'Units/Day', 'Days Until Stockout']}
                    rows={forecast.reorder_candidates.map((c) => [
                      c.title,
                      String(c.inventory),
                      String(c.daily_velocity),
                      String(c.days_until_stockout),
                    ])}
                  />
                )}
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

const PERIOD_OPTIONS = [
  { label: 'Last 14 days', value: '14' },
  { label: 'Last 30 days', value: '30' },
  { label: 'Last 60 days', value: '60' },
];

export function InventoryForecastPanel() {
  const {
    forecasts, activeForecast, loading, generating, error,
    total, page, lastPage, fetchForecasts, generate, deleteForecast, setActiveForecast, setError,
  } = useInventoryForecast();

  const [days, setDays] = useState('30');

  useEffect(() => {
    fetchForecasts(1);
  }, []);

  if (activeForecast) {
    return (
      <ForecastDetail
        forecast={activeForecast}
        onBack={() => setActiveForecast(null)}
        onDelete={deleteForecast}
      />
    );
  }

  const latest = forecasts[0];
  const highUrgencyCount = latest?.ai_recommendations.filter((r) => r.urgency === 'high').length ?? 0;

  return (
    <Page fullWidth
      title="Inventory Forecasting"
      subtitle="Projects days-until-stockout from real sales velocity so you can reorder bestsellers before they run out"
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          )}

          {latest && (
            <InlineGrid columns={{ xs: 1, sm: 3 }} gap="300">
              <StatCard label="Reorder Candidates" value={String(latest.reorder_candidates.length)} icon={InventoryIcon} iconColor="indigo" />
              <StatCard label="High Urgency" value={String(highUrgencyCount)} icon={AlertTriangleIcon} iconColor="orange" deltaLabel="Under 7 days of stock left" />
              <StatCard label="Forecasts Run" value={String(total)} icon={ListBulletedIcon} iconColor="teal" />
            </InlineGrid>
          )}

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Generate New Forecast</Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Analyses recent sales velocity against current stock levels to flag products that need reordering soon.
              </Text>
              <InlineStack gap="300" blockAlign="end">
                <Box minWidth="200px">
                  <Select label="Period" options={PERIOD_OPTIONS} value={days} onChange={setDays} />
                </Box>
                <Button
                  variant="primary"
                  loading={generating}
                  onClick={() => generate({ days: Number(days) })}
                >
                  Generate Forecast
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Past Forecasts ({total})</Text>

              {loading ? (
                <InlineStack align="center"><Spinner /></InlineStack>
              ) : forecasts.length === 0 ? (
                <EmptyState
                  heading="No inventory forecasts yet"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  action={{ content: 'Generate First Forecast', onAction: () => generate({ days: Number(days) }) }}
                >
                  <p>Generate a forecast to find products at risk of stocking out soon.</p>
                </EmptyState>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'numeric', 'numeric', 'text']}
                  headings={['Period', 'Orders Analysed', 'Recommendations', 'Actions']}
                  rows={forecasts.map((f) => [
                    <Button variant="plain" onClick={() => setActiveForecast(f)}>{`${f.period_start} – ${f.period_end}`}</Button>,
                    String(f.orders_analyzed),
                    String(f.ai_recommendations.length),
                    <Button variant="plain" tone="critical" size="slim" onClick={() => deleteForecast(f.id)}>Delete</Button>,
                  ])}
                />
              )}

              {lastPage > 1 && (
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    onPrevious={() => fetchForecasts(page - 1)}
                    hasNext={page < lastPage}
                    onNext={() => fetchForecasts(page + 1)}
                  />
                </InlineStack>
              )}
            </BlockStack>
          </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
